import React, { useMemo } from "react";
import { AreaChart as RAreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { getChartTheme, getPeakIndices } from "./chartTheme";
import { ChartEmpty } from "./BarChart";

/** data: [{ name, value }] */
const AreaChart = ({ data = [], theme = "light", color = "#8b5cf6", height = 280, noDataLabel = "No data", showOnlyPeaks = true }) => {
  const { grid, axis, tooltipStyle, labelStyle } = getChartTheme(theme);
  const gradientId = `areaGradient-${color.replace("#", "")}`;

  const peakIndices = useMemo(() => getPeakIndices(data), [data]);

  if (!data.length) return <ChartEmpty height={height} theme={theme} label={noDataLabel} />;

  // Rotate whenever more than one label will actually be drawn -- even
  // short labels collide when two peaks land on neighboring points, and
  // angled text takes up far less horizontal room per label.
  const visibleLabelCount = showOnlyPeaks ? peakIndices.size : data.length;
  const manyPoints = data.length > 10 || visibleLabelCount > 1;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <RAreaChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: manyPoints ? 22 : 0 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.45} />
            <stop offset="95%" stopColor={color} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
        <XAxis
          dataKey="name"
          tick={{ fill: axis, fontSize: 11 }}
          axisLine={{ stroke: grid }}
          tickLine={false}
          interval={0}
          angle={manyPoints ? -30 : 0}
          textAnchor={manyPoints ? "end" : "middle"}
          height={manyPoints ? 40 : 30}
          tickFormatter={(value, index) => (!showOnlyPeaks || peakIndices.has(index) ? value : "")}
        />
        <YAxis tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} width={36} />
        <Tooltip contentStyle={tooltipStyle} labelStyle={labelStyle} cursor={{ stroke: color, strokeWidth: 1 }} />
        <Area type="monotone" dataKey="value" stroke={color} strokeWidth={2.5} fill={`url(#${gradientId})`} animationDuration={600} />
      </RAreaChart>
    </ResponsiveContainer>
  );
};

export default AreaChart;
